import urlUtils from 'utils/Url';
import { BEAUTY_INSIDER_TERMS_LINK } from 'constants/legal';
import {
    HISTORY_CARD_STYLES,
    HISTORY_ROUTES,
    HISTORY_SECTION_STATUS,
    HISTORY_USER_STATES,
    PROGRAM_LINKS
} from 'components/Content/BeautyInsider/BeautyInsiderHistory/historyConstants';
import { mapHistoryYearAtGlanceItems } from 'components/Content/BeautyInsider/BeautyInsiderHistory/mapHistoryYearAtGlanceItems';
import mapHistoryRewardsRedeemed from 'components/Content/BeautyInsider/BeautyInsiderHistory/mapHistoryRewardsRedeemed';
import localeUtils from 'utils/LanguageLocale';
import HistoryTabEN from 'components/Content/BeautyInsider/BeautyInsiderHistory/locales/HistoryTab_en_US';
import HistoryTabFR from 'components/Content/BeautyInsider/BeautyInsiderHistory/locales/HistoryTab_fr_CA';
import Empty from 'constants/empty';

const getText = localeUtils.getLocalisedDataFromResource(HistoryTabEN, HistoryTabFR);

const sectionHasError = sectionData => Boolean(sectionData?.error);
const sectionIsLoading = sectionData => Boolean(sectionData?.loading || sectionData?.data === null);
const sectionIsUninitialized = sectionData => typeof sectionData?.data === 'undefined';

const getProfile = profile => ({
    isSignedIn: Boolean(profile?.isSignedIn),
    isRecognized: Boolean(profile?.isRecognized),
    isBi: Boolean(profile?.isBi)
});

const hasHistoryAccess = profile => profile.isSignedIn || profile.isRecognized;

const getUserState = ({ profile, yearItems, rewardsThumbnails, pointRecords }) => {
    if (!hasHistoryAccess(profile)) {
        return HISTORY_USER_STATES.GUEST;
    }

    if (!profile.isBi) {
        return HISTORY_USER_STATES.NON_BI;
    }

    const hasActivity = yearItems.length > 0 || rewardsThumbnails.length > 0 || pointRecords.length > 0;

    if (!hasActivity) {
        return HISTORY_USER_STATES.EMPTY_BI;
    }

    return HISTORY_USER_STATES.INSIDER;
};

const getPlaceholderCopy = (userState, signedOutCopy, nonBiCopy) => (userState === HISTORY_USER_STATES.GUEST ? signedOutCopy : nonBiCopy);

const getYear = yearAtGlance => yearAtGlance?.data?.currentYear?.year || yearAtGlance?.data?.year;
const isFunction = value => typeof value === 'function';

const buildYearAtGlanceSection = ({
    userState, yearItems, year, yearAtGlance, actions
}) => {
    const displayYear = year || new Date().getFullYear();
    const title = getText('yearAtGlanceTitle', [displayYear]);

    if (userState === HISTORY_USER_STATES.GUEST || (userState === HISTORY_USER_STATES.NON_BI && yearItems.length === 0)) {
        return {
            title,
            status: HISTORY_SECTION_STATUS.PLACEHOLDER,
            cardStyle: HISTORY_CARD_STYLES.GREY,
            items: [],
            placeholder: {
                body: getPlaceholderCopy(userState, getText('yearAtGlanceSignedOut', [displayYear]), getText('yearAtGlanceNonBi', [displayYear]))
            }
        };
    }

    if (sectionHasError(yearAtGlance) && yearItems.length === 0) {
        return {
            title,
            status: HISTORY_SECTION_STATUS.ERROR,
            cardStyle: HISTORY_CARD_STYLES.WHITE,
            items: [],
            error: { message: getText('yearAtGlanceError'), retryable: true },
            retry: actions.retryYearAtGlance
        };
    }

    if (sectionIsLoading(yearAtGlance)) {
        return {
            title,
            status: HISTORY_SECTION_STATUS.LOADING,
            cardStyle: HISTORY_CARD_STYLES.WHITE,
            items: []
        };
    }

    if (yearItems.length === 0) {
        return {
            title,
            status: HISTORY_SECTION_STATUS.EMPTY,
            cardStyle: HISTORY_CARD_STYLES.WHITE,
            items: [],
            emptyState: {
                illustration: '/img/ufe/no-rewards.svg',
                body: getText('yearAtGlanceEmpty', [displayYear]),
                primaryAction: {
                    label: getText('shopNow'),
                    action: actions.shopNow
                }
            }
        };
    }

    return {
        title,
        status: HISTORY_SECTION_STATUS.POPULATED,
        cardStyle: HISTORY_CARD_STYLES.WHITE,
        items: yearItems
    };
};

const buildRewardsRedeemedSection = ({ userState, rewardsRedeemed, rewardsThumbnails, actions }) => {
    const title = getText('rewardsRedeemedTitle');

    if (userState === HISTORY_USER_STATES.GUEST || userState === HISTORY_USER_STATES.NON_BI) {
        return {
            title,
            status: HISTORY_SECTION_STATUS.PLACEHOLDER,
            cardStyle: HISTORY_CARD_STYLES.GREY,
            thumbnails: [],
            placeholder: {
                body: getPlaceholderCopy(userState, getText('rewardsRedeemedSignedOut'), getText('rewardsRedeemedNonBi'))
            }
        };
    }

    if (sectionHasError(rewardsRedeemed) && rewardsThumbnails.length === 0) {
        const retry = isFunction(actions.retryRewardsRedeemed) ? actions.retryRewardsRedeemed : undefined;

        return {
            title,
            status: HISTORY_SECTION_STATUS.ERROR,
            cardStyle: HISTORY_CARD_STYLES.WHITE,
            thumbnails: [],
            error: { message: getText('rewardsRedeemedError'), retryable: Boolean(retry) },
            retry
        };
    }

    if (sectionIsLoading(rewardsRedeemed)) {
        return {
            title,
            status: HISTORY_SECTION_STATUS.LOADING,
            cardStyle: HISTORY_CARD_STYLES.WHITE,
            thumbnails: []
        };
    }

    if (rewardsThumbnails.length === 0) {
        return {
            title,
            status: HISTORY_SECTION_STATUS.EMPTY,
            cardStyle: HISTORY_CARD_STYLES.WHITE,
            thumbnails: [],
            emptyState: {
                illustration: '/img/ufe/no-redeemed.svg',
                body: getText('rewardsRedeemedEmpty')
            }
        };
    }

    return {
        title,
        status: HISTORY_SECTION_STATUS.POPULATED,
        cardStyle: HISTORY_CARD_STYLES.WHITE,
        thumbnails: rewardsThumbnails,
        viewDetail: {
            label: getText('viewDetails'),
            href: HISTORY_ROUTES.REWARDS_DETAIL
        }
    };
};

const buildPointsHistorySection = ({ userState, groupedPoints, pointRecords, actions }) => {
    const title = getText('pointsHistoryTitle');
    const infoAction = {
        label: getText('pointsHistoryInfoLabel'),
        action: actions.openPointsInfo
    };

    if (userState === HISTORY_USER_STATES.GUEST || userState === HISTORY_USER_STATES.NON_BI) {
        return {
            title,
            status: HISTORY_SECTION_STATUS.PLACEHOLDER,
            cardStyle: HISTORY_CARD_STYLES.GREY,
            groups: [],
            infoAction,
            placeholder: {
                body: getPlaceholderCopy(userState, getText('pointsHistorySignedOut'), getText('pointsHistoryNonBi'))
            }
        };
    }

    if (sectionHasError(groupedPoints) && pointRecords.length === 0) {
        const retry = isFunction(actions.retryPointsHistory) ? actions.retryPointsHistory : undefined;

        return {
            title,
            status: HISTORY_SECTION_STATUS.ERROR,
            cardStyle: HISTORY_CARD_STYLES.WHITE,
            groups: [],
            infoAction,
            error: { message: getText('pointsHistoryError'), retryable: Boolean(retry) },
            retry
        };
    }

    if (sectionIsLoading(groupedPoints) || sectionIsUninitialized(groupedPoints)) {
        return {
            title,
            status: HISTORY_SECTION_STATUS.LOADING,
            cardStyle: HISTORY_CARD_STYLES.WHITE,
            groups: [],
            infoAction
        };
    }

    if (pointRecords.length === 0) {
        return {
            title,
            status: HISTORY_SECTION_STATUS.EMPTY,
            cardStyle: HISTORY_CARD_STYLES.WHITE,
            groups: [],
            infoAction,
            disclaimer: getText('pointsDisclaimer'),
            emptyState: {
                illustration: '/img/ufe/no-points.svg',
                body: getText('pointsHistoryEmpty')
            }
        };
    }

    return {
        title,
        status: HISTORY_SECTION_STATUS.POPULATED,
        cardStyle: HISTORY_CARD_STYLES.WHITE,
        groups: pointRecords,
        infoAction,
        disclaimer: getText('pointsDisclaimer'),
        ...(pointRecords.length > 3 && {
            viewAll: {
                label: getText('viewAll'),
                href: HISTORY_ROUTES.POINTS_HISTORY,
                action: actions.openPointsViewAll
            }
        })
    };
};

const buildProgramLinksSection = actions => ({
    links: [
        {
            id: PROGRAM_LINKS.FAQS,
            label: getText('faqs'),
            href: HISTORY_ROUTES.FAQS,
            action: actions.openFaqs
        },
        {
            id: PROGRAM_LINKS.TERMS,
            label: getText('terms'),
            href: BEAUTY_INSIDER_TERMS_LINK,
            action: actions.openTerms
        }
    ]
});

const defaultActions = {
    retryYearAtGlance: () => {},
    retryPointsHistory: () => {},
    openPointsInfo: () => {},
    openPointsViewAll: () => {
        urlUtils.redirectTo(HISTORY_ROUTES.POINTS_HISTORY);
    },
    openFaqs: () => {
        urlUtils.redirectTo(HISTORY_ROUTES.FAQS);
    },
    openTerms: () => {
        urlUtils.redirectTo(BEAUTY_INSIDER_TERMS_LINK);
    },
    shopNow: () => {
        urlUtils.redirectTo(HISTORY_ROUTES.SHOP_NOW);
    }
};

/**
 * Maps raw BI History tab inputs into the presentation contract consumed by the Year at a Glance section.
 *
 * @param {Object} params
 * @param {Object} params.profile Recognized/sign-in/BI profile flags.
 * @param {'US'|'CA'} [params.region='US'] Region used for annual activity filtering.
 * @param {Object} [params.yearAtGlance={}] Year at a Glance DTO with loading/error flags and current-year summary data.
 * @param {Object} [params.rewardsRedeemed={}] Rewards redeemed DTO with loading/error flags and purchase-history groups.
 * @param {Object} [params.groupedPoints={}] Points activity DTO with loading/error flags and grouped point records.
 * @param {Object} [params.actions={}] Optional UI callbacks for retry, modal, route, and shop-now behavior.
 * @returns {Object} History user state and section presentation data.
 */
const mapHistoryTabPresentation = (
    {
        profile,
        region = 'US',
        yearAtGlance = Empty.Object,
        rewardsRedeemed = Empty.Object,
        groupedPoints = Empty.Object,
        actions = Empty.Object
    } = Empty.Object
) => {
    const normalizedProfile = getProfile(profile);
    const mergedActions = {
        ...defaultActions,
        ...actions
    };
    const yearItems = mapHistoryYearAtGlanceItems({
        rawItems: yearAtGlance.data,
        region,
        getText
    });
    const rewardsThumbnails = mapHistoryRewardsRedeemed(rewardsRedeemed.data);
    const pointRecords = Array.isArray(groupedPoints.data?.records)
        ? groupedPoints.data.records
        : Array.isArray(groupedPoints.data)
            ? groupedPoints.data
            : [];
    const userState = getUserState({
        profile: normalizedProfile,
        yearItems,
        rewardsThumbnails,
        pointRecords
    });

    return {
        userState,
        sections: {
            yearAtGlance: buildYearAtGlanceSection({
                userState,
                yearItems,
                year: getYear(yearAtGlance),
                yearAtGlance,
                actions: mergedActions
            }),
            rewardsRedeemed: buildRewardsRedeemedSection({
                userState,
                rewardsRedeemed,
                rewardsThumbnails,
                actions: mergedActions
            }),
            pointsHistory: buildPointsHistorySection({
                userState,
                groupedPoints,
                pointRecords,
                actions: mergedActions
            }),
            programLinks: buildProgramLinksSection(mergedActions)
        }
    };
};

export default mapHistoryTabPresentation;
